import {
  Chart,
  BarElement,
  BarController,
  LineController,
  LineElement,
  registerables,
} from 'chart.js';
import $ from 'jquery';

import { SColors } from '@/utils/constants';
import { commentMedium, commentWhiteBg } from '@/assets/icons';
import { globalEventDispatcher, ShizenGlobalEvent } from '@/utils/event-dispatcher/global';

Chart.register(
  BarElement,
  BarController,
  ...registerables,
);

const ChartDataLabels = require('chartjs-plugin-datalabels');

Chart.unregister(ChartDataLabels);
/* eslint-disable no-underscore-dangle */

function findChartSegmentEnd(start, end, points) {
  for (; end > start; end--) {
    const point = points[end];
    if (!isNaN(point.x) && !isNaN(point.y)) {
      break;
    }
  }
  return end;
}

/**
 * For Line chart with dual color, clip the area above/below before calling `draw`.
 * Stolen from Chart.js in filler.drawing.js `clipVertical`
 * @param  {Chart} chart                    chart object
 * @param  {CanvasRenderingContext2D} ctx   2D context
 * @param  {LineElement} line               Line element to draw above/below
 * @param  {Number} clipY                   Y-coordinate to close the path
 */
function clipChartForLine(chart, ctx, line, clipY) {
  const { points, _segments: segments } = line;
  const pointMargin = 7;
  let first = true;
  let lineLoop = false;
  ctx.beginPath();
  segments.forEach((segment) => {
    const { start, end } = segment;
    const firstPoint = points[start];
    const lastPoint = points[findChartSegmentEnd(start, end, points)];
    if (first) {
      ctx.moveTo(firstPoint.x - pointMargin, firstPoint.y);
      first = false;
    } else {
      ctx.lineTo(firstPoint.x - pointMargin, clipY);
      ctx.lineTo(firstPoint.x - pointMargin, firstPoint.y);
    }
    lineLoop = !!line.pathSegment(ctx, segment, { move: lineLoop });
    if (lineLoop) {
      ctx.closePath();
    } else {
      ctx.lineTo(lastPoint.x + pointMargin, clipY);
    }
  });
  ctx.lineTo(line.first().x - pointMargin, clipY);
  ctx.closePath();
  ctx.clip();
}

class DualLineController extends LineController {
  draw(...args) {
    const { chart } = this;
    const meta = this.getMeta();
    if (!meta._dataset.complexBorderColor || !(meta._dataset.complexBorderColor instanceof Object)) {
      super.draw(...args);
      return;
    }
    const { complexBorderColor } = meta._dataset;

    // Copied from plugin.filler/index.js in 'afterDatasetsUpdate' to identify all potential targets
    // to limit the border lines
    const count = (chart.data.datasets || []).length;
    const sources = [];
    for (let i = 0; i < count; ++i) {
      const sourceMeta = chart.getDatasetMeta(i);
      const sourceLine = sourceMeta.dataset;
      let source = null;
      if (sourceLine && sourceLine.options && sourceLine instanceof LineElement) {
        source = {
          visible: chart.isDatasetVisible(i),
          index: i,
          chart,
          axis: sourceMeta.controller.options.indexAxis,
          scale: sourceMeta.vScale,
          line: sourceLine,
        };
      }
      sources.push(source);
    }

    // Clip above and below to draw the line
    const target = sources[complexBorderColor.target];
    const { ctx, chartArea } = chart;
    if (target) {
      const { line: targetLine } = target;
      ctx.save();
      // Clip below the objective line, then draw
      clipChartForLine(chart, ctx, targetLine, chartArea.bottom);
      meta._dataset.borderColor = complexBorderColor.below;
      meta.dataset.options.borderColor = complexBorderColor.below;
      super.draw(...args);
      ctx.restore();
      ctx.save();
      // Clip above the objective line, then draw
      clipChartForLine(chart, ctx, targetLine, chartArea.top);
      meta._dataset.borderColor = complexBorderColor.above;
      meta.dataset.options.borderColor = complexBorderColor.above;
      super.draw(...args);
      ctx.restore();
    }
  }
}
DualLineController.id = 'dualLine';
DualLineController.defaults = LineController.defaults;

Chart.register(DualLineController);

Chart.defaults.commentIcon = new Image();
const DOMURL = window.URL || window.webkitURL || window;
// WARNING: This relies on commentMedium size (from icons/index.js)
const commentIconSvg = new Blob(
  [`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="24" width="24">
      <path d="${commentWhiteBg}" style="fill: #fff"></path>
      <path d="${commentMedium}" style="fill: ${SColors.mainTextColor}"></path>
    </svg>
  `],
  { type: 'image/svg+xml' },
);
const commentIconUrl = DOMURL.createObjectURL(commentIconSvg);
Chart.defaults.commentIcon.onload = () => {
  DOMURL.revokeObjectURL(commentIconUrl);
};
Chart.defaults.commentIcon.src = commentIconUrl;

/**
 * Chart.js plugin to add a comment icon above indicator values that have comments
 * For additionnal ideas :
 * https://stackoverflow.com/questions/39283177/programmatically-open-and-close-chart-js-tooltip
 */
Chart.register({
  id: 'commentIcon',
  afterInit: (chart) => {
    chart.activeCommentTootltip = null;
    chart.commentExistsAtIndex = [];
    chart.commentsMouseInside = [];
    chart.commentsEventToReceive = [];
    const data = chart.data.datasets[0] ? chart.data.datasets[0].data : [];
    const { length } = data;
    for (let i = 0; i < length; i++) {
      chart.commentExistsAtIndex.push(false);
      chart.commentsMouseInside.push(false);
      chart.commentsEventToReceive.push(false);
    }
  },
  afterDraw: (chart) => {
    $(chart.canvas).off('mousemove.comments');
    $(chart.canvas).off('touchmove.comments');
    $(chart.canvas).off('mouseenter.comments');
    $(chart.canvas).off('touchstart.comments');

    if (chart.config.options.indicatorComments) {
      const { ctx } = chart;
      const dataset = chart.data.datasets[0];
      const obj = chart.getDatasetMeta(0);
      for (let i = 0; i < dataset.data.length; i++) {
        const model = obj.data[i];
        if (typeof dataset.data[i] !== 'undefined' && dataset.data[i] != null) {
          const comments = chart.config.options.indicatorComments[i];
          if (comments instanceof Array && comments.length > 0) {
            if (!chart.commentExistsAtIndex) {
              chart.commentExistsAtIndex = [];
            }
            chart.commentExistsAtIndex[i] = true;

            const finalIdx = i;
            const width = 16;
            const halfWidth = width / 2;
            const { canvas } = chart;
            const comPosition = { x: model.x - halfWidth, y: model.y + 5 };
            ctx.drawImage(Chart.defaults.commentIcon, comPosition.x, comPosition.y, width, width);

            const handler = (e) => {
              if (!chart.canvas || e.originalEvent.createdByShizenCommentIcon) {
                return;
              }
              let { clientX, clientY } = e;
              if (e.touches && e.touches.length) {
                /* eslint-disable-next-line prefer-destructuring */
                clientX = e.touches[0].clientX;
                /* eslint-disable-next-line prefer-destructuring */
                clientY = e.touches[0].clientY;
              }
              const rect = chart.canvas.getBoundingClientRect();
              const x = clientX - rect.left;
              const y = clientY - rect.top;
              if (
                x > comPosition.x
                && x < comPosition.x + width
                && y > comPosition.y
                && y < comPosition.y + width
              ) {
                if (!chart.commentsMouseInside[finalIdx]) {
                  const mouseMoveEvent = new MouseEvent('mousemove', {
                    clientX: rect.left + model.x + 2,
                    clientY: rect.top + model.y + 2,
                  });
                  mouseMoveEvent.createdByShizenCommentIcon = true;
                  chart.commentsMouseInside[finalIdx] = true;
                  chart.commentsEventToReceive[finalIdx] = true;
                  chart.canvas.dispatchEvent(mouseMoveEvent);
                }
                e.preventDefault();
                e.stopPropagation();
              } else if (chart.commentsEventToReceive[finalIdx]) {
                chart.commentsEventToReceive[finalIdx] = false;
                e.preventDefault();
                e.stopPropagation();
              } else if (chart.commentsMouseInside[finalIdx]) {
                chart.commentsMouseInside[finalIdx] = false;
                chart.tooltip.update(true);
              }
            };
            $(canvas).on('mouseenter.comments', handler);
            $(canvas).on('mousemove.comments', handler);
            $(canvas).on('touchstart.comments', handler);
            $(canvas).on('touchmove.comments', handler);
          }
        }
      }
    }
  },
});

/**
 * Rounded doughnut
 * Credits: https://stackoverflow.com/questions/67968358/chart-js-v3-doughnut-with-rounded-edges-but-not-everywhere
 */
Chart.register({
  id: 'roundedDoughnut',

  beforeDraw(chart) {
    if (!chart.config.options.elements || !chart.config.options.elements.center) {
      return;
    }
    const { ctx } = chart;
    const {
      fontStyle = 'Work Sans',
      fontWeight = '500',
      text,
      color,
      maxFontSize = 35,
      sidePadding = 20,
      minFontSize = 7,
      lineHeight = 25,
    } = chart.config.options.elements.center;
    const { innerRadius } = chart._metasets[chart._metasets.length - 1].data[0];
    const sidePaddingCalculated = (sidePadding / 100) * (innerRadius * 2);
    // Start with a base font of 30px
    ctx.font = `30px ${fontStyle}`;

    // Get the width of the string and also the width of the element minus 10 to give it 5px side padding
    const stringWidth = ctx.measureText(text).width;
    const elementWidth = (innerRadius * 2) - sidePaddingCalculated;

    // Find out how much the font can grow in width.
    const widthRatio = elementWidth / stringWidth;
    const newFontSize = Math.floor(30 * widthRatio);
    const elementHeight = (innerRadius * 2);

    // Pick a new font size so it will not be larger than the height of label.
    /* eslint-disable-next-line */
    const wrapText = minFontSize && fontSizeToUse < minFontSize;
    const fontSizeToUse = wrapText ? minFontSize : Math.min(newFontSize, elementHeight, maxFontSize);

    // Set font settings to draw it correctly.
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const centerX = ((chart.chartArea.left + chart.chartArea.right) / 2);
    let centerY = ((chart.chartArea.top + chart.chartArea.bottom) / 2);
    ctx.font = `${fontWeight} ${fontSizeToUse}px ${fontStyle}`;
    ctx.fillStyle = color;
    if (!wrapText) {
      ctx.fillText(text, centerX, centerY);
      return;
    }

    const words = text.split(' ');
    const lines = [];
    let line = '';
    // Break words up into multiple lines if necessary
    for (let n = 0; n < words.length; n++) {
      const testLine = `${line + words[n]} `;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > elementWidth && n > 0) {
        lines.push(line);
        line = `${words[n]} `;
      } else {
        line = testLine;
      }
    }

    // Move the center up depending on line height and number of lines
    centerY -= (lines.length / 2) * lineHeight;
    for (let n = 0; n < lines.length; n++) {
      ctx.fillText(lines[n], centerX, centerY);
      centerY += lineHeight;
    }
    // Draw text in center
    ctx.fillText(line, centerX, centerY);
  },

  afterUpdate(chart) {
    if (chart.options.elements.arc.roundedCornersFor !== undefined) {
      const arcValues = Object.values(chart.options.elements.arc.roundedCornersFor);
      arcValues.forEach((arcs) => {
        arcs = Array.isArray(arcs) ? arcs : [arcs];
        arcs.forEach((i) => {
          const arc = chart.getDatasetMeta(0).data[i];
          arc.round = {
            x: (chart.chartArea.left + chart.chartArea.right) / 2,
            y: (chart.chartArea.top + chart.chartArea.bottom) / 2,
            radius: (arc.outerRadius + arc.innerRadius) / 2,
            thickness: (arc.outerRadius - arc.innerRadius) / 2,
            backgroundColor: arc.options.backgroundColor,
          };
        });
      });
    }
  },

  afterDraw: (chart) => {
    if (chart.options.elements.arc.roundedCornersFor !== undefined) {
      const { ctx } = chart;
      const { roundedCornersFor } = chart.options.elements.arc;
      /* eslint-disable-next-line */
      for (const position in roundedCornersFor) {
        const values = Array.isArray(roundedCornersFor[position])
          ? roundedCornersFor[position]
          : [roundedCornersFor[position]];
        values.forEach((p) => {
          const arc = chart.getDatasetMeta(0).data[p];
          if (arc) {
            const startAngle = Math.PI / 2 - arc.startAngle;
            const endAngle = Math.PI / 2 - arc.endAngle;
            ctx.save();
            ctx.translate(arc.round.x, arc.round.y);
            ctx.fillStyle = arc.options.backgroundColor;
            ctx.beginPath();
            if (position === 'start') {
              ctx.arc(
                arc.round.radius * Math.sin(startAngle),
                arc.round.radius * Math.cos(startAngle),
                arc.round.thickness,
                0,
                2 * Math.PI,
              );
            } else {
              ctx.arc(
                arc.round.radius * Math.sin(endAngle),
                arc.round.radius * Math.cos(endAngle),
                arc.round.thickness,
                0,
                2 * Math.PI,
              );
            }
            ctx.closePath();
            ctx.fill();
            ctx.restore();
          }
        });
      }
    }
  },
});


/** As of july 2018, charts can have two types : bar or line.
For a line chart, if a value exceeds the objective value, the area between
the line chart and the objective is filled with red.
For a bar chart, the entire bar is red */
Chart.defaults.smallFontSize = 10;
Chart.defaults.defaultFontSize = 12;
Chart.defaults.defaultFontFamily = 'Georgia';


// Handle hiding the tooltip when clicking outside any canvas
const tooltipMouseDownHandler = (e) => {
  if (!$(e.target).is('canvas.chartjs-render-monitor')) {
    globalEventDispatcher.dispatch(ShizenGlobalEvent.CHART_TOOLTIP_HIDE);
  }
};
$(document.body).on('mousedown.tooltip', tooltipMouseDownHandler);
$(document.body).on('touchstart.tooltip', tooltipMouseDownHandler);

// Display the custom tooltip instead of the default one
Chart.defaults.plugins.tooltip.enabled = false;
Chart.defaults.plugins.tooltip.callbacks.label = () => null;
Chart.overrides.doughnut.plugins.tooltip.callbacks.label = () => null;
Chart.defaults.plugins.tooltip.displayColors = false;
/* eslint-disable-next-line func-names */
Chart.defaults.plugins.tooltip.external = function ({ tooltip }) {
  if (
    !tooltip.options.enabled
    && (!tooltip.title || !tooltip.title.length)
    && (!tooltip.body || !tooltip.body.length || !tooltip.body[0].lines.length)
  ) {
    return;
  }
  const active = this._active[0];
  const chart = this._chart;
  if (active && active.index !== undefined) {
    if (tooltip.opacity === 1 && chart.commentExistsAtIndex[active.index]) {
      chart.commentsMouseInside[active.index] = true;
    }
    if (chart.commentsMouseInside[active.index]) {
      chart.activeCommentTootltip = active.index;
    }
  }

  // Remove the previous fullscreen event
  const nclParams = { clickable: false, canvas: chart.canvas };
  globalEventDispatcher.dispatch(ShizenGlobalEvent.CHART_TOOLTIP_SET_CLICKABLE, nclParams);
  // Hovering out. Mouse is not over a point neither over a comment
  if (
    !(chart.activeCommentTootltip !== undefined && chart.commentsMouseInside[chart.activeCommentTootltip])
    && !tooltip.opacity
  ) {
    globalEventDispatcher.dispatch(ShizenGlobalEvent.CHART_TOOLTIP_HIDE);
    return;
  }
  // Tooltip position
  const rect = $(chart.canvas)[0].getBoundingClientRect();
  const params = { rect, tooltip };
  globalEventDispatcher.dispatch(ShizenGlobalEvent.CHART_TOOLTIP_SHOW, params);
  tooltip.opacity = 0;

  // Display the tooltip content in a modal when clicking the tooltip or the chart point
  // If active is undefined, mouse is hovering a comment icon
  if (!active || chart.commentExistsAtIndex[active.index]) {
    const clParams = { clickable: true, canvas: chart.canvas };
    globalEventDispatcher.dispatch(ShizenGlobalEvent.CHART_TOOLTIP_SET_CLICKABLE, clParams);
  }
};
// Hide the tooltip div
Chart.register({
  id: 'globallyHideTooltip',
  destroy() {
    globalEventDispatcher.dispatch(ShizenGlobalEvent.CHART_TOOLTIP_HIDE);
  },
});

Chart.defaults.plugins.legend.display = false;
Chart.defaults.animation.duration = 0;
